import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Nearest ancestor that actually scrolls vertically: overflow-y auto/scroll
 * AND taller content than box — a Radix ScrollArea viewport computes
 * overflow-y scroll even when it only ever scrolls the x axis, and must not
 * shadow the real vertical scroller around it.
 */
export const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    )
      return node;
    node = node.parentElement;
  }
  return null;
};

export const useSticky = ({
  triggerOn,
  offset = 0,
  withinScrollContainer = false,
}: {
  triggerOn: "top" | "bottom" | "inView";
  offset?: number;
  /**
   * Measure against the ref's nearest scroll container instead of the
   * viewport (e.g. inside a bottom sheet), and catch its scroll events.
   */
  withinScrollContainer?: boolean;
}) => {
  const ref = useRef(null);
  const [hasReachedRef, setHasReachedRef] = useState(false);

  const fixateBar = useCallback(() => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const container = withinScrollContainer
      ? findScrollContainer(ref.current)
      : null;
    const containerRect = container?.getBoundingClientRect();
    // The "viewport" edges the trigger is measured against.
    const top = containerRect?.top ?? 0;
    const bottom = containerRect?.bottom ?? window.innerHeight;

    if (triggerOn === "inView") {
      // Element is in view when its top enters viewport and bottom hasn't left viewport
      setHasReachedRef(
        rect.top <= bottom + offset && rect.bottom >= top + offset,
      );
    } else if (triggerOn === "top") {
      // If triggerOn is "top", we check if the top of the element is entering from the bottom of the viewport
      const trigger = rect.top; // Distance from the top of the viewport to the top of the element
      setHasReachedRef(trigger <= bottom + offset && trigger >= top + offset);
    } else {
      // If triggerOn is "bottom", we check if the bottom of the element hits the top of the viewport
      const trigger = rect.bottom; // Distance from the top of the viewport to the bottom of the element
      setHasReachedRef(trigger <= top + offset);
    }
  }, [triggerOn, offset, withinScrollContainer]);

  useEffect(() => {
    // Scroll events don't bubble, so a plain window listener misses inner
    // scroll containers — listen in the capture phase when we care about one.
    window.addEventListener("scroll", fixateBar, withinScrollContainer);
    // Run once on mount to check initial position
    fixateBar();
    return () => {
      window.removeEventListener("scroll", fixateBar, withinScrollContainer);
    };
  }, [fixateBar, withinScrollContainer]);

  return [hasReachedRef, ref];
};
